import { useTranslation } from 'react-i18next';

import type { DiagramSeriesOption } from '../../types/DiagramSeries.types';
import { formatDiagramSeriesLabel } from '../../utils/formatDiagramSeriesLabel';
import { SeriesColorMarker } from '../series-color-marker/SeriesColorMarker';
import { DiagramSeriesChipsRoot, SeriesChip } from './DiagramSeriesChips.style';

export type DiagramSeriesChipsProps = {
  series: DiagramSeriesOption[];
  highlightedSeriesIds: string[];
  getSeriesColor: (seriesId: string) => string;
  onHighlightToggle: (seriesId: string) => void;
  onDeselect: (seriesId: string) => void;
};

export function DiagramSeriesChips({
  series,
  highlightedSeriesIds,
  getSeriesColor,
  onHighlightToggle,
  onDeselect,
}: DiagramSeriesChipsProps) {
  const { t } = useTranslation();
  const highlightedIds = new Set(highlightedSeriesIds);

  if (series.length === 0) {
    return null;
  }

  return (
    <DiagramSeriesChipsRoot
      direction="row"
      role="group"
      aria-label={t('timeSeriesChart.chips.label')}
      data-qa-element-id="time-series-chart-series-chips"
    >
      {series.map((item) => {
        const color = getSeriesColor(item.id);
        const isHighlighted = highlightedIds.has(item.id);

        return (
          <SeriesChip
            key={item.id}
            variant="outlined"
            size="small"
            seriesColor={color}
            isHighlighted={isHighlighted}
            valueType={item.valueType}
            aria-pressed={isHighlighted}
            icon={<SeriesColorMarker seriesColor={color} valueType={item.valueType} />}
            label={formatDiagramSeriesLabel(item)}
            onClick={() => onHighlightToggle(item.id)}
            onDelete={() => onDeselect(item.id)}
          />
        );
      })}
    </DiagramSeriesChipsRoot>
  );
}
